import { useState, useCallback, type RefObject } from 'react';

export interface UploaderConfig {
  fileTypes?: string[];
  maxLength?: number;
  multiple?: boolean;
  maxFileSize?: number; // in bytes
}

export interface UseUploaderReturn {
  files: File[];
  setFiles: (files: File[]) => void;
  errors: string[];
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearFiles: () => void;
  clearErrors: () => void;
}

const useUploader = (
  inputRef: RefObject<HTMLInputElement | null>,
  config: UploaderConfig = {}
): UseUploaderReturn => {
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const validateFile = useCallback(
    (file: File): string | null => {
      // Validate file type
      if (config.fileTypes && config.fileTypes.length > 0) {
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        const isValidType = config.fileTypes.some(
          type => type.toLowerCase() === fileExtension
        );
        if (!isValidType) {
          return `File type not allowed. Allowed types: ${config.fileTypes.join(', ')}`;
        }
      }

      // Validate file size
      if (config.maxFileSize && file.size > config.maxFileSize) {
        const maxSizeMB = (config.maxFileSize / (1024 * 1024)).toFixed(2);
        return `File size exceeds maximum allowed size of ${maxSizeMB}MB`;
      }

      return null;
    },
    [config.fileTypes, config.maxFileSize]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e.target.files;
      if (!selectedFiles || selectedFiles.length === 0) {
        return;
      }

      const newErrors: string[] = [];
      const validFiles: File[] = [];

      const filesArray = Array.from(selectedFiles);

      // Validate multiple files limit
      if (config.maxLength && filesArray.length > config.maxLength) {
        newErrors.push(
          `Maximum ${config.maxLength} file(s) allowed. You selected ${filesArray.length} file(s).`
        );
        setErrors(newErrors);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
        return;
      }

      // Validate each file
      filesArray.forEach(file => {
        const error = validateFile(file);
        if (error) {
          newErrors.push(`${file.name}: ${error}`);
        } else {
          validFiles.push(file);
        }
      });

      // Set errors if any
      if (newErrors.length > 0) {
        setErrors(newErrors);
      } else {
        setErrors([]);
      }

      // Set files based on multiple config
      if (config.multiple) {
        setFiles(prevFiles => [...prevFiles, ...validFiles]);
      } else {
        setFiles(validFiles.length > 0 ? [validFiles[0]] : []);
      }

      // Reset input value to allow selecting the same file again
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [config, validateFile, inputRef]
  );

  const clearFiles = useCallback(() => {
    setFiles([]);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [inputRef]);

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  return {
    files,
    setFiles,
    errors,
    handleFileChange,
    clearFiles,
    clearErrors,
  };
};

export default useUploader;
